/* eslint-disable react/no-array-index-key */
import {
  Alert,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Box,
  Flex,
  IconButton,
  Grid,
  GridItem,
  HStack,
  Progress,
  Input,
  Button,
  VStack,
  useColorModeValue,
  useColorMode,
  ColorMode,
} from '@chakra-ui/react';
import {
  Cell,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';

import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { DataDownloadControl } from './DataDownloadControl';
import { DownloadButton } from './DownloadButton';
import { TextFilter } from './Filter';
import { PopoverDownloadButton } from './PopoverDownloads';
import {
  AssetVerificationReceivableContainer,
  AssetVerificationReceivable,
  EditAssetVerification,
  LenderVerificationStatus,
  LenderVerificationStatusValues,
  AssetVerificationSummaryCounts,
  BaseFieldConfigMap,
} from './types';
import {
  createTanstackColumnConfig,
  getVerificationField,
  getVerificationFieldResult,
} from './utils';
import { DownloadIconButton } from 'packages/common-frontend/components/IconButton';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { TanStackPaginationControl } from 'packages/common-frontend/components/TanStack';
import { displayBaseFieldValue } from 'packages/common-frontend/utils';

const getStatusColor = (
  status: LenderVerificationStatus,
): 'green' | 'red' | 'orange' => {
  switch (status) {
    case 'untested':
    case 'finleyReview':
      return 'orange';

    case 'failed':
      return 'red';

    case 'passed':
      return 'green';
    default:
      return finleyAssertUnreachable(status);
  }
};

const getTitleText = (status: LenderVerificationStatus): string => {
  switch (status) {
    case 'untested':
      return 'Untested Receivables';
    case 'finleyReview':
      return 'Under Finley Review';
    case 'failed':
      return 'Failed Verification';
    case 'passed':
      return 'Passed Verification';
    default:
      return finleyAssertUnreachable(status);
  }
};

const getValidationIcon = (verificationStatus: LenderVerificationStatus) => {
  const color = getStatusColor(verificationStatus);
  switch (verificationStatus) {
    case 'passed':
      return <FaIcon icon="shield-check" color={color} size="large" />;
    case 'failed':
      return <FaIcon icon="shield-xmark" color={color} size="large" />;
    case 'untested':
      return <FaIcon icon="shield-exclamation" color={color} size="large" />;
    case 'finleyReview':
      return <FaIcon icon="shield-slash" color={color} size="large" />;
    default:
      return finleyAssertUnreachable(verificationStatus);
  }
};

const getRowBGColor = (
  colorMode: ColorMode,
  cell: Cell<AssetVerificationReceivable, any>,
  columns: ColumnDef<AssetVerificationReceivable, any>[],
) => {
  if (!_.includes(_.map(columns, 'id'), cell.column.id)) {
    return 'none';
  }

  const { verificationStatus } = cell.row.original;
  if (['Untested', 'TextractFailed'].includes(verificationStatus)) {
    // in these cases we do not have data about
    // specific fields.
    return colorMode === 'dark' ? 'orange.900' : 'orange.50';
  }

  const verifiedField = _.find(
    cell.row.original.fields,
    ({ field }) => field === cell.column.id,
  );

  finleyAssert(
    verifiedField,
    `Expected verified field [${cell.column.id}] to exist`,
  );

  const verificationResult = getVerificationFieldResult(verifiedField);
  switch (verificationResult) {
    case false:
      return colorMode === 'dark' ? 'red.900' : 'red.50';
    case null:
      // an individual field can be untested, but won't affect the overall verification status, so an individual field could be colored orange
      return colorMode === 'dark' ? 'orange.900' : 'orange.50';
    case true:
      return 'none';
    default:
      return finleyAssertUnreachable(verificationResult);
  }
};

// this components help modify the cell value.
type CellModifierProps = {
  verified: boolean | null;
  receivableDocumentationRawId: string | null;
};
const CellModifier = ({
  receivableDocumentationRawId,
  verified,
}: CellModifierProps) => {
  // eslint-disable-next-line no-console
  const handleClick = () => console.log('clicked');

  // if receivableDocumentationRawId is not null
  // it means we have a pdf document for this.
  // for admin mode we will allow editing of such fields
  // if verified is not true.
  // verified = true - the verification passed
  // verified = null - not verified (no pdf, or sanitize failed)
  // verified = false - verification failed
  if (!receivableDocumentationRawId || verified) {
    return null;
  }

  return (
    <VStack>
      <Input type="text" placeholder={receivableDocumentationRawId} />
      <Button h="1.75rem" size="sm" onClick={handleClick}>
        Update
      </Button>
    </VStack>
  );
};

// TODO:@asset_verification: use this component to edit cell values
// instead of full-scale json editor.
type VerifiedValueCellEditorProps = {
  value: any;
  verified: boolean | null;
  columnName?: string;
  verificationValue: string | null;
  receivableDocumentationRawId: string | null;
};
export const VerifiedValueCellEditor = ({
  receivableDocumentationRawId,
  value,
  verified,
  verificationValue,
  columnName,
}: VerifiedValueCellEditorProps) => {
  const isUnverified = verified === false;
  // if receivableDocumentationRawId is not null
  // it means we have a pdf document for this.
  // for admin mode we will allow editing of such fields
  // if verified is not true.
  // verified = true - the verification passed
  // verified = null - not verified (no pdf, or sanitize failed)
  // verified = false - verification failed
  return (
    <>
      <Text pb={isUnverified ? '16px' : undefined}>{value}</Text>
      {verificationValue && !verified && (
        <Text fontSize="xs" color="gray">{`The ${
          columnName || 'field'
        } is "${verificationValue}" in the Asset Data`}</Text>
      )}
      <CellModifier
        verified={verified}
        receivableDocumentationRawId={receivableDocumentationRawId}
      />
    </>
  );
};

type VerifiedValueCellProps = {
  value: any;
  verified: boolean | null;
  columnName?: string;
  verificationValue: string | null;
};
const VerifiedValueCell = ({
  value,
  verified,
  verificationValue,
  columnName,
}: VerifiedValueCellProps) => {
  const isUnverified = verified === false;
  return (
    <>
      <Text pb={isUnverified ? '16px' : undefined}>{value}</Text>
      {isUnverified && (
        <Text fontSize="xs" color="gray">{`The ${
          columnName || 'field'
        } is "${verificationValue}" in the Asset Data`}</Text>
      )}
    </>
  );
};

type InfoCardProps = {
  value: number;
  total: number;
  type: LenderVerificationStatus;
  onStatusClick: () => void;
  selected: boolean;
};
const InfoCard = ({
  value,
  total,
  type,
  selected,
  onStatusClick,
}: InfoCardProps) => {
  const borderColor = useColorModeValue('gray.500', 'gray.200');
  const progessColorScheme = getStatusColor(type);
  const progressBgColor = useColorModeValue(
    `${progessColorScheme}.100`,
    `${progessColorScheme}.700`,
  );
  return (
    <Grid
      gridTemplateColumns="repeat(8, 1fr)"
      borderRadius="8px"
      p="16px 24px"
      h="112px"
      w="400px"
      border="2px solid"
      borderColor={selected ? borderColor : 'transparent'}
      onClick={onStatusClick}
      _hover={{ cursor: 'pointer' }}
    >
      <GridItem gridRow="1/3" gridColumn="1" m="auto 0">
        {getValidationIcon(type)}
      </GridItem>
      <GridItem gridRow="1" gridColumn="2/-1">
        <Text size="small">{getTitleText(type)}</Text>
      </GridItem>
      <GridItem gridRow="2" gridColumn="2/-1">
        <Text>
          {value}/{total}
        </Text>
      </GridItem>
      <GridItem gridRow="3" gridColumn="1/-1">
        <Progress
          value={(100 * value) / total}
          colorScheme={getStatusColor(type)}
          borderRadius="8px"
          mt="8px"
          size="sm"
          bgColor={progressBgColor}
        />
      </GridItem>
    </Grid>
  );
};

type AssetVerificationStatusSelectorProps = {
  selectedType: LenderVerificationStatus;
  setSelectedType: (type: LenderVerificationStatus) => void;
  summaryCounts: AssetVerificationSummaryCounts;
};
export const AssetVerificationStatusSelector = ({
  selectedType,
  setSelectedType,
  summaryCounts,
}: AssetVerificationStatusSelectorProps) => {
  const total = Object.values(summaryCounts).reduce(
    (acc, value) => acc + value,
    0,
  );

  return (
    <HStack m="24px 0 40px">
      {LenderVerificationStatusValues.map((statusValue) => (
        <InfoCard
          key={statusValue}
          value={summaryCounts[statusValue]}
          total={total}
          type={statusValue}
          selected={selectedType === statusValue}
          onStatusClick={() => setSelectedType(statusValue)}
        />
      ))}
    </HStack>
  );
};

type AssetVerificationReceivablesStatusTableProps = {
  artifactDocumentId: string;
  creditFacilityId: string;
  baseFieldConfigsMap: BaseFieldConfigMap;
  receivables: AssetVerificationReceivable[];
  adminMode: boolean;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  onEdit: EditAssetVerification;
};
export const AssetVerificationReceivablesStatusTable = ({
  onEdit,
  baseFieldConfigsMap,
  receivables,
  adminMode,
  artifactDocumentId,
  creditFacilityId,
  reportingDate,
  fileVersion,
}: AssetVerificationReceivablesStatusTableProps) => {
  const { colorMode } = useColorMode();
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const columns: ColumnDef<AssetVerificationReceivable, any>[] = useMemo(() => {
    const baseFieldKeys = _.keys(baseFieldConfigsMap);
    return _.map(baseFieldKeys, (baseFieldKey) => {
      const baseFieldConfig = baseFieldConfigsMap[baseFieldKey];
      finleyAssert(
        baseFieldConfig,
        `Expected base field config to exist for ${baseFieldKey}`,
      );
      return createTanstackColumnConfig<AssetVerificationReceivable, any>({
        baseFieldKey,
        accessorFn: function accessor(row) {
          return getVerificationField(baseFieldKey, row.fields).loanTapeValue;
        },
        cell: function cell({ column, row, getValue }) {
          const verifiedField = getVerificationField(
            baseFieldKey,
            row.original.fields,
          );
          const transformValue = displayBaseFieldValue(
            baseFieldConfigsMap[baseFieldKey],
          );
          return (
            <VerifiedValueCell
              value={transformValue(getValue())}
              columnName={String(column.columnDef.header)}
              verified={getVerificationFieldResult(verifiedField)}
              verificationValue={verifiedField.result?.docRawValue ?? null}
            />
          );
        },
        enableColumnFilter: false, // TODO add in filter
        enableSorting: true,
      });
    });
  }, [baseFieldConfigsMap]);

  const adminModeColumns: Array<
    ColumnDef<AssetVerificationReceivable>
  > = adminMode
    ? [
        {
          id: 'editDoc',
          accessorKey: 'rawIdsWithTimestamps',
          header: 'Edit Doc',
          cell: function cell({ getValue, row }) {
            const rawIds = getValue<
              AssetVerificationReceivable['rawIdsWithTimestamps']
            >();
            // find latest doc raw id
            const latestDocRawId = _.maxBy(rawIds, 'timestamp')?.id;

            return latestDocRawId ? (
              <IconButton
                onClick={() =>
                  onEdit({
                    receivableDocumentationRawId: latestDocRawId,
                    creditFacilityId,
                    artifactDocumentId,
                    finleyReceivableId: row.original.finleyReceivableId,
                    reportingDate,
                    fileVersion,
                  })
                }
                variant="ghost"
                aria-label="download"
              >
                <FaIcon icon="pen-to-square" color="white" />
              </IconButton>
            ) : (
              '-'
            );
          },
          meta: {
            style: {
              whiteSpace: 'nowrap',
            },
          },
          enableSorting: false,
          enableColumnFilter: true,
        },
      ]
    : [];

  const table = useReactTable({
    data: receivables,
    columns: [
      // Required Columns
      ...adminModeColumns,
      {
        id: 'externalReceivableId',
        accessorKey: 'externalReceivableId',
        header: 'External Receivable ID',
        cell: function cell({ getValue }) {
          return getValue();
        },
        filterFn: ({ getValue }, colId, filterValue) => {
          if (filterValue) {
            const regex = new RegExp(filterValue, 'i');
            return regex.test(getValue<string>(colId));
          }
          // if we don't have a receivableId, everything passes
          return true;
        },
        meta: {
          style: {
            whiteSpace: 'nowrap',
          },
        },
        enableSorting: false,
        enableColumnFilter: true,
      },
      {
        id: 'downloadOptions',
        accessorKey: 'rawIdsWithTimestamps',
        header: '',
        cell: function cell({ getValue }) {
          const items = getValue<
            AssetVerificationReceivable['rawIdsWithTimestamps']
          >();

          if (items.length === 0) {
            return (
              <DownloadIconButton
                isDisabled
                ariaLabel="Download receivable verification documentation"
                label="Download"
              />
            );
          }
          if (items.length === 1) {
            return (
              <DownloadButton
                artifactDocumentId={artifactDocumentId}
                creditFacilityId={creditFacilityId}
                receivableDocumentationRawId={items[0].id}
              />
            );
          }
          // when we have multiple items
          // we need to show a popover tooltip.
          return (
            <PopoverDownloadButton
              items={items}
              artifactDocumentId={artifactDocumentId}
              creditFacilityId={creditFacilityId}
            />
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'lenderVerificationStatus',
        accessorKey: 'lenderVerificationStatus',
        header: '',
        cell: function cell({ getValue }) {
          return getValidationIcon(getValue());
        },
        sortingFn: (
          rowA: Row<AssetVerificationReceivable>,
          rowB: Row<AssetVerificationReceivable>,
          columnId,
        ) => {
          const getOrder = (
            lenderVerificationStatus: LenderVerificationStatus,
          ): number => {
            switch (lenderVerificationStatus) {
              case 'passed':
                return 3;
              case 'failed':
                return 0;
              case 'untested':
                return 1;
              case 'finleyReview':
                return 2;
              default:
                return finleyAssertUnreachable(lenderVerificationStatus);
            }
          };

          // eslint-disable-next-line finley/variable-length
          const a = getOrder(rowA.getValue(columnId));
          // eslint-disable-next-line finley/variable-length
          const b = getOrder(rowB.getValue(columnId));

          return a - b;
        },
        sortDescFirst: true,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: 'uploadTimeStamp',
        accessorKey: 'rawIdsWithTimestamps',
        header: 'Document Upload Date',
        cell: function cell({ getValue }) {
          const rawIds = getValue<
            AssetVerificationReceivable['rawIdsWithTimestamps']
          >();
          // find latest doc timestamp
          const maxTimeStamp = _.maxBy(rawIds, 'timestamp')?.timestamp;

          if (maxTimeStamp) {
            const dateObject = new Date(maxTimeStamp);
            const date = DateTime.fromJSDate(dateObject).setZone(
              'America/New_York',
            );
            return date.toLocaleString(DateTime.DATETIME_FULL);
          }
          return '-';
        },
        meta: {
          style: {
            whiteSpace: 'nowrap',
          },
        },
      },
      ...columns,
    ],
    initialState: {
      sorting: [
        {
          id: 'lenderVerificationStatus',
          desc: false,
        },
      ],
    },
    state: {
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <>
      <Table>
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canColumnSort = header.column.getCanSort();
                const canColumnFilter = header.column.getCanFilter();

                return (
                  <Th key={header.id}>
                    <Flex alignItems="center">
                      <Box
                        cursor={canColumnSort ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        mr="8px"
                      >
                        {{
                          asc: <FaIcon icon="sort-up" />,
                          desc: <FaIcon icon="sort-down" />,
                        }[header.column.getIsSorted() as string] ??
                          (canColumnSort ? <FaIcon icon="sort" /> : null)}
                      </Box>
                      <Box>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Box>
                      {canColumnFilter && <TextFilter column={header.column} />}
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <Td
                    key={cell.id}
                    bgColor={getRowBGColor(colorMode, cell, columns)}
                    style={cell.column.columnDef?.meta?.style}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
      <TanStackPaginationControl table={table} />
    </>
  );
};

type AssetVerificationReceivablesTableProps = {
  adminMode: boolean;
  receivablesVerification: AssetVerificationReceivableContainer;
  onEdit: EditAssetVerification;
  artifactDocumentId: string;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
};
export const AssetVerificationReceivablesTable = ({
  artifactDocumentId,
  creditFacilityId,
  receivablesVerification,
  reportingDate,
  fileVersion,
  adminMode,
  onEdit,
}: AssetVerificationReceivablesTableProps) => {
  const [
    selectedType,
    setSelectedType,
  ] = React.useState<LenderVerificationStatus>('finleyReview');
  return (
    <Flex direction="column" overflowX="auto" gap="1rem">
      <AssetVerificationStatusSelector
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        summaryCounts={receivablesVerification.summaryCounts}
      />
      {adminMode && (
        <DataDownloadControl
          receivables={receivablesVerification.statusReceivables[selectedType]}
          label={selectedType}
        />
      )}
      <AssetVerificationReceivablesStatusTable
        adminMode={adminMode}
        onEdit={onEdit}
        baseFieldConfigsMap={receivablesVerification.baseFieldConfigsMap}
        receivables={receivablesVerification.statusReceivables[selectedType]}
        artifactDocumentId={artifactDocumentId}
        creditFacilityId={creditFacilityId}
        reportingDate={reportingDate}
        fileVersion={fileVersion}
      />
    </Flex>
  );
};

type AssetVerificationReceivablesSectionProps = {
  adminMode: boolean;
  receivablesVerification: AssetVerificationReceivableContainer | null;
  onEdit: EditAssetVerification;
  artifactDocumentId: string;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
};
export const AssetVerificationReceivablesSection = ({
  artifactDocumentId,
  creditFacilityId,
  receivablesVerification,
  reportingDate,
  fileVersion,
  adminMode,
  onEdit,
}: AssetVerificationReceivablesSectionProps) => {
  const backgroundColor = useColorModeValue('white', 'gray.950');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <>
      {receivablesVerification ? (
        <Box
          borderRadius="8px"
          border="1px solid"
          borderColor={borderColor}
          backgroundColor={backgroundColor}
          padding="24px"
        >
          <AssetVerificationReceivablesTable
            adminMode={adminMode}
            onEdit={onEdit}
            receivablesVerification={receivablesVerification}
            artifactDocumentId={artifactDocumentId}
            creditFacilityId={creditFacilityId}
            reportingDate={reportingDate}
            fileVersion={fileVersion}
          />
        </Box>
      ) : (
        <>
          <Alert status="info"> Processing receivable documentation... </Alert>
          <Progress size="xs" isIndeterminate />
        </>
      )}
    </>
  );
};
